import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

const STORAGE_KEY = 'mate_admin_session'

// Origins allowed to post the "mate-auth" OIDC result message back to this app.
// NEVER add "*" here — that would let any page on the internet hand us a token.
const TRUSTED_TOKEN_ORIGINS = [
  // Staging's branded host is mat-e-s. It was mat-e-dev, which turned out never
  // to be a Worker domain at all — a leftover DNS record still serving the
  // retired Coolify build — so trusting it here would accept a token handed
  // back by a deployment we no longer run.
  'https://mat-e-s.asminternational.org', // branded staging callback
  'https://mat-e.asminternational.org', // branded production callback
  'https://asmi-content-chatbot-frontend-staging.asmintl.workers.dev', // staging callback
  'https://asmi-content-chatbot-frontend-production.asmintl.workers.dev', // production callback
]

export interface AdminUser {
  user_id: string
  email?: string
  name?: string
}

interface StoredSession {
  token: string
  user_id: string
  email?: string
  name?: string
  exp?: number
}

interface AuthContextValue {
  token: string | null
  user: AdminUser | null
  isAuthenticated: boolean
  isSigningIn: boolean
  login: () => void
  logout: () => void
}

// exp is unix-seconds; a session with no exp is treated as valid (non-expiring).
function isSessionValid(session: StoredSession | null | undefined): session is StoredSession {
  if (!session || !session.token || !session.user_id) return false
  if (session.exp == null) return true
  return session.exp * 1000 > Date.now()
}

function readStoredSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredSession
    if (!isSessionValid(parsed)) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/**
 * Plain (non-React) accessor so the fetch/api layer can attach the bearer
 * token without needing to live inside the component tree.
 */
export function getStoredAdminToken(): string | null {
  return readStoredSession()?.token ?? null
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => readStoredSession())
  const [isSigningIn, setIsSigningIn] = useState(false)
  const cleanupRef = useRef<() => void>(() => {})

  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    setSession(null)
  }, [])

  // Fired by the API layer when a request comes back 401 — the token is
  // stale/invalid, so drop the local session and fall back to the login screen.
  useEffect(() => {
    const handleUnauthorized = () => clearSession()
    window.addEventListener('mate-admin-unauthorized', handleUnauthorized)
    return () => window.removeEventListener('mate-admin-unauthorized', handleUnauthorized)
  }, [clearSession])

  // Stop listening for the popup's postMessage / polling for it closing on unmount.
  useEffect(() => () => cleanupRef.current(), [])

  const login = useCallback(() => {
    cleanupRef.current()

    const popup = window.open(
      `${import.meta.env.VITE_API_URL}/api/auth/oidc/authorize`,
      'mate-admin-signin',
      'popup,width=480,height=760'
    )
    setIsSigningIn(true)

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'mate-auth') return
      if (!TRUSTED_TOKEN_ORIGINS.includes(event.origin)) {
        console.warn(`[auth] ignored mate-auth message from untrusted origin: ${event.origin}`)
        return
      }

      const { token, user_id, email, name, exp } = event.data
      if (!token || !user_id) return

      const next: StoredSession = { token, user_id, email, name, exp }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      setSession(next)
      setIsSigningIn(false)
      popup?.close()
      cleanup()
    }

    window.addEventListener('message', handleMessage)

    // If the user closes the popup without finishing sign-in, stop showing the spinner.
    const pollId = window.setInterval(() => {
      if (popup?.closed) {
        setIsSigningIn(false)
        cleanup()
      }
    }, 500)

    function cleanup() {
      window.removeEventListener('message', handleMessage)
      window.clearInterval(pollId)
      cleanupRef.current = () => {}
    }

    cleanupRef.current = cleanup
  }, [])

  const logout = useCallback(() => {
    clearSession()
  }, [clearSession])

  const value: AuthContextValue = {
    token: session?.token ?? null,
    user: session
      ? { user_id: session.user_id, email: session.email, name: session.name }
      : null,
    isAuthenticated: isSessionValid(session),
    isSigningIn,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
